import { Add } from '@mui/icons-material';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import {
  isFunctionAttribute,
  type IMeasure,
} from '../../../Interfaces/ILegalTransactionMeasure';
import { Dispatch, SetStateAction, useState } from 'react';
import { MeasureCard } from './MeasureCard';
import { CreateOrUpdateMeasureDialog } from './CreateOrUpdateMeasureDialog';
import { ImportMeasuresDialog } from './ImportMeasuresDialog';
import type { ILegalRequirement } from '../../../Interfaces/ILegalRequirement';
import type { ILegalRequirementTemplate } from '../../../Interfaces/ILegalRequirementTemplate';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';

export const MeasuresList = ({
  legalRequirements,
  legalRequirementTemplates,
  measures = [],
  specialFunctions = [],
  setSpecialFunctions,
  gwgs = [],
  setGwgs,
  setMeasures,
}: {
  legalRequirements: ILegalRequirement[];
  legalRequirementTemplates: ILegalRequirementTemplate[];
  measures?: IMeasure[];
  specialFunctions?: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setSpecialFunctions?: Dispatch<
    SetStateAction<ILegalTransaction_has_LegalTransactionSpecialFunction[]>
  >;
  gwgs?: ILegalTransactionGwGFullObject[];
  setGwgs?: Dispatch<SetStateAction<ILegalTransactionGwGFullObject[]>>;
  setMeasures: Dispatch<SetStateAction<IMeasure[]>>;
}) => {
  const theme = useTheme();
  const [showAddMeasureDialog, setShowAddMeasureDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  const [editedMeasure, setEditedMeasure] = useState<IMeasure | null>(null);

  const handleOpenAddMeasureDialog = () => {
    setShowAddMeasureDialog(true);
  };

  const handleAddMeasure = (measure: IMeasure) => {
    setMeasures([...measures, measure]);
    setShowAddMeasureDialog(false);
  };

  const handleImportMeasures = (importedMeasures: IMeasure[]) => {
    setMeasures([...measures, ...importedMeasures]);
    setShowImportDialog(false);
  };

  const handleDeleteMeasure = (measureId: number) => {
    const measure = measures.find(m => m.idMeasurement === measureId);
    if (measure) {
      measure.attributes.forEach(attribute => {
        if (isFunctionAttribute(attribute)) {
          if (attribute.idSF !== 2 && specialFunctions && setSpecialFunctions) {
            setSpecialFunctions(
              specialFunctions.filter(
                sf =>
                  sf.idLegalTransaction_has_LegalTransactionSpecialFunction !==
                  attribute.Value
              )
            );
          }
          if (attribute.idSF === 2 && gwgs && setGwgs) {
            setGwgs(
              gwgs.filter(gwg => gwg.idLegalTransactionGwG !== attribute.Value)
            );
          }
        }
      });
    }
    setMeasures(
      measures.filter(measure => measure.idMeasurement !== measureId)
    );
  };

  const handleUpdateMeasure = (measure: IMeasure) => {
    setMeasures(
      measures.map(m =>
        m.idMeasurement === measure.idMeasurement ? measure : m
      )
    );
    setEditedMeasure(null);
  };

  const handleMoveUp = (measure: IMeasure) => {
    if (!measures) return;
    const index = measures.findIndex(
      m => m.idMeasurement === measure.idMeasurement
    );
    if (index <= 0) return;

    const newMeasures = [...measures];
    [newMeasures[index - 1], newMeasures[index]] = [
      newMeasures[index],
      newMeasures[index - 1],
    ];
    setMeasures(newMeasures);
  };

  const handleMoveDown = (measure: IMeasure) => {
    if (!measures) return;
    const index = measures.findIndex(
      m => m.idMeasurement === measure.idMeasurement
    );
    if (index >= measures.length - 1) return;

    const newMeasures = [...measures];
    [newMeasures[index], newMeasures[index + 1]] = [
      newMeasures[index + 1],
      newMeasures[index],
    ];
    setMeasures(newMeasures);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h5'>Vorgangsmanager</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button variant='outlined' onClick={() => setShowImportDialog(true)}>
            Aus Rechtsgeschäft importieren
          </Button>
          <IconButton onClick={handleOpenAddMeasureDialog}>
            <Add />
          </IconButton>
        </Box>
      </Box>
      <Box mt={2}>
        {!measures || measures.length === 0 ? (
          <Box
            sx={{
              height: '40vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.grey[50],
              borderRadius: 4,
              gap: 4,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='h6' textAlign='center'>
                Keine Vorgangsmanager-Positionen vorhanden
              </Typography>
              <Typography variant='body1' textAlign='center'>
                Noch sind keine Vorgangsmanager-Positionen vorhanden.
              </Typography>
            </Box>
            <Button
              variant='contained'
              color='primary'
              onClick={handleOpenAddMeasureDialog}
            >
              Vorgangsmanager-Position hinzufügen
            </Button>
          </Box>
        ) : (
          <Box display='flex' flexDirection='column' gap={1}>
            {measures.map((measure, index) => (
              <MeasureCard
                key={measure.idMeasurement}
                measure={measure}
                onDelete={handleDeleteMeasure}
                onEdit={() => setEditedMeasure(measure)}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isFirst={index === 0}
                isLast={index === measures.length - 1}
              />
            ))}
          </Box>
        )}
      </Box>
      {editedMeasure && (
        <CreateOrUpdateMeasureDialog
          legalRequirements={legalRequirements}
          legalRequirementTemplates={legalRequirementTemplates}
          initialMeasure={editedMeasure}
          gwgs={gwgs}
          specialFunctions={specialFunctions}
          setSpecialFunctions={setSpecialFunctions}
          setGwgs={setGwgs}
          onClose={() => setEditedMeasure(null)}
          onCreateOrUpdateMeasure={handleUpdateMeasure}
        />
      )}

      {showAddMeasureDialog && (
        <CreateOrUpdateMeasureDialog
          legalRequirements={legalRequirements}
          legalRequirementTemplates={legalRequirementTemplates}
          gwgs={gwgs}
          specialFunctions={specialFunctions}
          setSpecialFunctions={setSpecialFunctions}
          setGwgs={setGwgs}
          onClose={() => setShowAddMeasureDialog(false)}
          onCreateOrUpdateMeasure={handleAddMeasure}
        />
      )}

      {showImportDialog && (
        <ImportMeasuresDialog
          onClose={() => setShowImportDialog(false)}
          onImportMeasures={handleImportMeasures}
        />
      )}
    </>
  );
};
